import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Building2, ArrowLeft, Plus, X, MapPin, Globe, Mail, Phone, User, Briefcase } from "lucide-react";

const STATUS_STYLE = { Active: "bg-emerald-100 text-emerald-700", Prospect: "bg-amber-100 text-amber-700", Inactive: "bg-slate-200 text-slate-600" };
const REC_STATUSES = ["Recommended", "Client Review", "Shortlisted", "Client Interview", "Selected", "Rejected", "Withdrawn", "On Hold"];
const REC_STYLE = {
  Recommended: "bg-blue-100 text-blue-700", "Client Review": "bg-indigo-100 text-indigo-700",
  Shortlisted: "bg-violet-100 text-violet-700", "Client Interview": "bg-amber-100 text-amber-700",
  Selected: "bg-emerald-100 text-emerald-700", Rejected: "bg-red-100 text-red-600",
  Withdrawn: "bg-slate-200 text-slate-600", "On Hold": "bg-orange-100 text-orange-700", Draft: "bg-slate-100 text-slate-500",
};
const Stat = ({ label, value }) => (
  <div className="text-center px-2"><div className="num font-extrabold text-2xl text-ink">{value}</div><div className="text-[11px] text-ink-faint uppercase tracking-wide mt-0.5">{label}</div></div>
);

function AddJobModal({ clientId, roles, onClose, onCreated }) {
  const [f, setF] = useState({ client_id: clientId, title: "", role_id: "", description: "", location: "", experience: "3-6 years", employment_type: "Full-time", salary_range: "", priority: "Medium", status: "Open", required_skills: [], preferred_skills: [] });
  const [busy, setBusy] = useState(false);
  const pickRole = (rid) => { const r = roles.find((x) => x.id === rid); setF({ ...f, role_id: rid, title: r ? r.name : f.title, description: r ? r.description : f.description, required_skills: r ? r.required_skills.slice(0, 5) : [] }); };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Job title is required");
    setBusy(true);
    try { const { data } = await api.post("/agency/jobs", f); toast.success("Job created"); onCreated(data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-lift w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><div className="font-heading font-bold text-ink text-lg">Create Job Requirement</div><button onClick={onClose}><X className="h-5 w-5 text-ink-faint" /></button></div>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-ink-soft">Target Role</label>
            <select data-testid="job-role" value={f.role_id} onChange={(e) => pickRole(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
              <option value="">— Select role —</option>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select></div>
          <div><label className="text-xs font-semibold text-ink-soft">Job Title *</label>
            <input data-testid="job-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-ink-soft">Experience</label><input value={f.experience} onChange={(e) => setF({ ...f, experience: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" /></div>
            <div><label className="text-xs font-semibold text-ink-soft">Employment Type</label>
              <select value={f.employment_type} onChange={(e) => setF({ ...f, employment_type: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"><option>Full-time</option><option>Contract</option><option>Internship</option></select></div>
            <div><label className="text-xs font-semibold text-ink-soft">Salary Range</label><input value={f.salary_range} onChange={(e) => setF({ ...f, salary_range: e.target.value })} placeholder="₹15-24 LPA" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" /></div>
            <div><label className="text-xs font-semibold text-ink-soft">Priority</label>
              <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"><option>High</option><option>Medium</option><option>Low</option></select></div>
          </div>
          <div><label className="text-xs font-semibold text-ink-soft">Required Skills (comma separated)</label>
            <input data-testid="job-skills" value={f.required_skills.join(", ")} onChange={(e) => setF({ ...f, required_skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand" /></div>
        </div>
        <button data-testid="save-job" onClick={save} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Job"}</button>
      </div>
    </div>
  );
}

export default function AgencyClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const [roles, setRoles] = useState([]);
  const [modal, setModal] = useState(false);
  const load = () => api.get(`/agency/clients/${id}`).then((r) => setD(r.data)).catch(() => { toast.error("Client not found"); navigate("/agency/clients"); });
  useEffect(() => { load(); api.get("/catalog/job-roles").then((r) => setRoles(r.data)).catch(() => setRoles([])); }, [id]);

  const updateRecStatus = async (rid, status) => {
    try { await api.put(`/agency/recommendations/${rid}`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="min-h-[40vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  const c = d.client;
  const TABS = [["overview", "Overview"], ["jobs", `Jobs (${d.jobs.length})`], ["candidates", `Pipeline (${d.recommended.length})`]];

  return (
    <div className="space-y-6" data-testid="agency-client-profile">
      <button onClick={() => navigate("/agency/clients")} className="inline-flex items-center gap-2 text-sm font-bold text-ink-soft hover:text-ink"><ArrowLeft className="h-4 w-4" /> Back to Clients</button>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="h-14 w-14 rounded-2xl bg-brand-light grid place-items-center"><Building2 className="h-7 w-7 text-brand" /></span>
            <div>
              <div className="flex items-center gap-2"><h1 className="font-heading text-2xl font-bold text-ink">{c.name}</h1>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${STATUS_STYLE[c.status]}`}>{c.status}</span></div>
              <div className="text-sm text-ink-soft mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span>{c.industry}</span>
                {c.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.location}</span>}
                {c.website && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {c.website}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-4 rounded-xl bg-slate-50 px-4 py-3">
            <Stat label="Active" value={c.active_candidates} /><Stat label="Recommended" value={c.recommended} /><Stat label="Interviews" value={c.interviews} /><Stat label="Selected" value={c.selected} />
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === k ? "border-brand text-brand" : "border-transparent text-ink-faint hover:text-ink"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-heading font-bold text-ink mb-3">Primary Contact</div>
            <div className="space-y-2 text-sm text-ink-soft">
              <div className="flex items-center gap-2"><User className="h-4 w-4 text-ink-faint" /> {c.contact_name || "—"}</div>
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-ink-faint" /> {c.contact_email || "—"}</div>
              <div className="flex items-center gap-2 num"><Phone className="h-4 w-4 text-ink-faint" /> {c.contact_phone || "—"}</div>
              <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-ink-faint" /> Account Manager: <span className="font-semibold text-ink">{c.account_manager || "—"}</span></div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="font-heading font-bold text-ink mb-3">Notes</div>
            <p className="text-sm text-ink-soft">{c.notes || "No notes added."}</p>
          </div>
        </div>
      )}

      {tab === "jobs" && (
        <div className="space-y-4">
          <div className="flex justify-end"><button data-testid="add-job-btn" onClick={() => setModal(true)} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2 rounded-full transition-colors"><Plus className="h-4 w-4" /> Create Job</button></div>
          {d.jobs.length === 0 ? <div className="text-center text-ink-soft py-12">No job requirements yet.</div> : (
            <div className="grid sm:grid-cols-2 gap-4">
              {d.jobs.map((j) => (
                <div key={j.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`job-${j.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="font-heading font-bold text-ink">{j.title}</div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${j.priority === "High" ? "bg-red-100 text-red-700" : j.priority === "Medium" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{j.priority}</span>
                  </div>
                  <div className="text-xs text-ink-faint mt-1">{j.experience} · {j.employment_type} · {j.salary_range || "—"}</div>
                  <div className="flex flex-wrap gap-1 mt-3">{(j.required_skills || []).slice(0, 6).map((s) => <span key={s} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-ink-soft">{s}</span>)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "candidates" && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-ink-faint">
              <tr><th className="px-5 py-3">Candidate</th><th className="px-5 py-3">Position</th><th className="px-5 py-3">Einstein Score™</th><th className="px-5 py-3">Date</th><th className="px-5 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.recommended.map((r) => (
                <tr key={r.recommendation_id} data-testid={`rec-${r.recommendation_id}`}>
                  <td className="px-5 py-3.5 font-semibold text-ink">{r.candidate_name}</td>
                  <td className="px-5 py-3.5 text-ink-soft">{r.job_title || r.target_role_name}</td>
                  <td className="px-5 py-3.5"><span className="num font-bold text-ink">{r.einstein_score}</span><span className="text-ink-faint">/100</span></td>
                  <td className="px-5 py-3.5 text-ink-soft num">{r.date}</td>
                  <td className="px-5 py-3.5">
                    <select value={r.status} onChange={(e) => updateRecStatus(r.recommendation_id, e.target.value)} data-testid={`client-rec-status-${r.recommendation_id}`} className={`text-[10px] font-bold px-2.5 py-1 rounded-full cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-brand ${REC_STYLE[r.status] || REC_STYLE.Draft}`}>
                      {REC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {d.recommended.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-ink-soft">No candidates recommended to this client yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {modal && <AddJobModal clientId={id} roles={roles} onClose={() => setModal(false)} onCreated={() => { setModal(false); load(); }} />}
    </div>
  );
}
